import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import { Typography, Menu, MenuItem, Button } from '@sm/wds-react';
import { IconCaretDown } from '@sm/wds-icons';
import { Logo } from '@sm/webassets';
import { defineMessages, T } from '@sm/intl';
// LinkWithSearch should be used so it will keep any existing query arguments present in the url (needed for ut_source)
import LinkWithSearch from '../LinkWithSearch';
import switchAccountsUrl from './switchAccountsUrl';

const COPY = defineMessages({
  NAV_MY_ACCOUNT: {
    id: 'AppsDirectory.AppDirNavHeaderDesktop.MyAccountLink',
    defaultMessage: 'My Account',
    description: '[Type: Link][Vis.: Low] - A link that will direct the user to their profile page',
  },
  NAV_SWITCH_ACCOUNTS: {
    id: 'AppsDirectory.AppDirNavHeaderDesktop.SwitchAccountsLink',
    defaultMessage: 'Switch accounts',
    description: '[Type: Label][Vis.: high] - A link that will direct the user to the switch accounts page',
  },
  NAV_HELP_CENTER: {
    id: 'AppsDirectory.AppDirNavHeaderDesktop.HelpCenterLink',
    defaultMessage: 'Help Center',
    description: '[Type: Link][Vis.: Low] - A link that will direct the user to the help center page',
  },
  NAV_SIGN_OUT: {
    id: 'AppsDirectory.AppDirNavHeaderDesktop.SignOutLink',
    defaultMessage: 'Sign Out',
    description: '[Type: Link][Vis.: Low] - A link that will sign the user out',
  },
  NAV_SIGN_IN: {
    id: 'AppsDirectory.AppDirNavHeaderDesktop.SignInLink',
    defaultMessage: 'Sign In',
    description: '[Type: Link][Vis.: Low] - A link that will direct the user to the sign in page',
  },
  NAV_SIGN_UP: {
    id: 'AppsDirectory.AppDirNavHeaderDesktop.SignUpLink',
    defaultMessage: 'Sign Up',
    description: '[Type: Link][Vis.: Low] - A link that will direct the user to the sign up page',
  },
});

const AppDirNavHeaderDesktop = ({
  navItems,
  shouldDisplayNavItem,
  isAuthenticated,
  username,
  showSwitchAccounts,
  location: { pathname, search },
}) => {
  return (
    <div className="sm-app-dir-nav-header wds-p-t-3 wds-p-b-3 wds-flex wds-flex--y-center wds-flex--between">
      <LinkWithSearch className="sm-app-dir-nav-header__title wds-flex wds-flex--y-center" pathname="/apps/">
        <Logo color="alt" />
        <Typography variant="sectionTitle" color="light" className="wds-m-l-3">
          App Directory
        </Typography>
      </LinkWithSearch>
      <div className="wds-flex">
        <nav className="sm-app-dir-nav-header__nav">
          <ul className="sm-app-dir-nav-header__nav-list wds-flex">
            {navItems.map(nav => (
              <If condition={shouldDisplayNavItem(nav)} key={nav.target}>
                <li className="sm-app-dir-nav-header__nav-list-item wds-m-l-7" key={nav.target}>
                  <a href={`${nav.target}${search}`} className="sm-app-dir-nav-header__nav-list-item-link">
                    {nav.text}
                  </a>
                </li>
              </If>
            ))}
          </ul>
        </nav>
      </div>
      <Choose>
        <When condition={isAuthenticated}>
          <div className="sm-app-dir-nav-header__nav-user">
            <Menu
              autoClose
              noRules
              dense
              placement="bottom-right"
              menuTrigger={
                <div
                  className="sm-app-dir-nav-header__nav-user-trigger"
                  data-testid="AppDirNavHeaderDesktop__UserMenuTrigger"
                >
                  <span className="wds-m-r-2">{username}</span>
                  <IconCaretDown />
                </div>
              }
            >
              <MenuItem
                isNavigable
                navigationTarget={`/user/account${search}`}
                className="sm-app-dir-nav-header__nav-list-item-link"
              >
                <T desc={COPY.NAV_MY_ACCOUNT} />
              </MenuItem>
              <If condition={showSwitchAccounts}>
                <MenuItem isNavigable navigationTarget={switchAccountsUrl(pathname, search)}>
                  <T desc={COPY.NAV_SWITCH_ACCOUNTS} />
                </MenuItem>
              </If>
              <MenuItem isNavigable navigationTarget="https://help.surveymonkey.com/?ut_source=header">
                <T desc={COPY.NAV_HELP_CENTER} />
              </MenuItem>
              <MenuItem isNavigable navigationTarget={`/user/sign-out${search}`}>
                <T desc={COPY.NAV_SIGN_OUT} />
              </MenuItem>
            </Menu>
          </div>
        </When>
        <Otherwise>
          <div>
            <Button href={`/user/sign-in${search}`} className="wds-m-r-3">
              <T desc={COPY.NAV_SIGN_IN} />
            </Button>
            <Button className="sm-app-dir-nav-header__button--bordered" href={`/user/sign-up${search}`}>
              <T desc={COPY.NAV_SIGN_UP} />
            </Button>
          </div>
        </Otherwise>
      </Choose>
    </div>
  );
};

AppDirNavHeaderDesktop.propTypes = {
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string.isRequired,
      text: PropTypes.node.isRequired,
      requiresAuthentication: PropTypes.bool,
    })
  ).isRequired,
  shouldDisplayNavItem: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  username: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  showSwitchAccounts: PropTypes.bool,
};

AppDirNavHeaderDesktop.defaultProps = {
  showSwitchAccounts: false,
};

export default withRouter(AppDirNavHeaderDesktop);
